import React, { useRef } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { API_URL } from '../api';

const cookies = new Cookies();
const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

/**
 * Drop-in attachment picker for any admin-side email/messaging composer.
 * Holds its own file-input ref; the parent owns the attachments array.
 *
 * Props:
 *   value      — array of { url, filename, mime, size }
 *   onChange   — setter for value
 *   onError    — optional callback called with an error message string
 *   folder     — Cloudinary subfolder for the upload (defaults to "emails")
 *   maxBytes   — single-file cap (defaults to 25 MB)
 *   disabled   — bool
 *   helpText   — optional caption below the button
 */
export default function EmailAttachmentsInput({
    value = [],
    onChange,
    onError,
    folder = 'emails',
    maxBytes = 25 * 1024 * 1024,
    disabled = false,
    helpText,
}) {
    const inputRef = useRef(null);

    const reportError = (msg) => {
        if (onError) onError(msg);
        else console.log(msg);
    };

    const handle = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > maxBytes) {
            reportError(`File is larger than ${Math.round(maxBytes / 1024 / 1024)} MB.`);
            e.target.value = '';
            return;
        }
        try {
            const reader = new FileReader();
            reader.onload = async () => {
                try {
                    const { data } = await axios.post(
                        `${API_URL}/api/admin/uploads/file`,
                        { base64: reader.result, filename: file.name, folder },
                        authHeaders()
                    );
                    onChange([
                        ...value,
                        {
                            url: data.url,
                            filename: data.filename || file.name,
                            mime: file.type,
                            size: data.size || file.size,
                        },
                    ]);
                } catch (err) {
                    reportError(err?.response?.data?.message || 'Upload failed.');
                }
            };
            reader.readAsDataURL(file);
        } finally {
            e.target.value = '';
        }
    };

    const remove = (i) => onChange(value.filter((_, j) => j !== i));

    return (
        <Box>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Button
                    type="button"
                    variant="outlined"
                    size="small"
                    startIcon={<AttachFileIcon />}
                    onClick={() => inputRef.current?.click()}
                    disabled={disabled}
                >
                    Attach file
                </Button>
                <input type="file" hidden ref={inputRef} onChange={handle} />
                {helpText && (
                    <Typography variant="caption" color="text.secondary">{helpText}</Typography>
                )}
            </Stack>
            {value.length > 0 && (
                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                    {value.map((a, i) => (
                        <Chip
                            key={i}
                            label={a.filename}
                            onDelete={() => remove(i)}
                            size="small"
                            variant="outlined"
                        />
                    ))}
                </Stack>
            )}
        </Box>
    );
}
